import { useState } from "react";
import {
  createInvitation,
  getInvitation,
  claimInvitation,
  cancelInvitation,
  ApiError,
} from "../services/gridApi";

interface UmaInvitation {
  code: string;
  createdAt: string;
  url: string;
  inviterUma: string;
  inviteeUma?: string;
  status: "PENDING" | "CLAIMED" | "EXPIRED" | "CANCELLED";
  firstName?: string;
  amountToSend?: number;
}

type ActiveTab = "create" | "lookup" | "claim" | "cancel";

const statusColors: Record<string, { bg: string; text: string }> = {
  PENDING: { bg: "bg-yellow-500/10", text: "text-yellow-400" },
  CLAIMED: { bg: "bg-emerald-500/10", text: "text-emerald-400" },
  CANCELLED: { bg: "bg-[#222222]", text: "text-[#888888]" },
  EXPIRED: { bg: "bg-red-500/10", text: "text-red-400" },
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function Invitations() {
  const [activeTab, setActiveTab] = useState<ActiveTab>("create");

  // Create form
  const [inviterUma, setInviterUma] = useState("");
  const [firstName, setFirstName] = useState("");
  const [amountToSend, setAmountToSend] = useState("");

  // Lookup form
  const [lookupCode, setLookupCode] = useState("");

  // Claim form
  const [claimCode, setClaimCode] = useState("");
  const [inviteeUma, setInviteeUma] = useState("");

  // Cancel form
  const [cancelCode, setCancelCode] = useState("");

  // Shared state
  const [result, setResult] = useState<UmaInvitation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const clearResult = () => {
    setResult(null);
    setError(null);
  };

  const handleCreate = async () => {
    clearResult();
    setLoading(true);
    try {
      const body: Parameters<typeof createInvitation>[0] = {
        inviterUma,
      };
      if (firstName.trim()) body.firstName = firstName.trim();
      if (amountToSend) body.amountToSend = Number(amountToSend);
      const inv = await createInvitation(body);
      setResult(inv);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async () => {
    clearResult();
    setLoading(true);
    try {
      const inv = await getInvitation(lookupCode.trim());
      setResult(inv);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    clearResult();
    setLoading(true);
    try {
      const inv = await claimInvitation(claimCode.trim(), inviteeUma.trim());
      setResult(inv);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    clearResult();
    setLoading(true);
    try {
      const inv = await cancelInvitation(cancelCode.trim());
      setResult(inv);
    } catch (err) {
      setError(err instanceof ApiError ? `${err.status}: ${err.message}` : String(err));
    } finally {
      setLoading(false);
    }
  };

  const tabs: { key: ActiveTab; label: string }[] = [
    { key: "create", label: "Create" },
    { key: "lookup", label: "Lookup" },
    { key: "claim", label: "Claim" },
    { key: "cancel", label: "Cancel" },
  ];

  return (
    <div className="p-8 bg-black min-h-screen">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">
          Invitations
        </h1>
        <p className="text-sm text-[#888888] mt-1">
          Manage UMA invitations -- create, look up, claim, and cancel
        </p>
      </div>

      {/* Tab Bar */}
      <div className="flex items-center gap-1 bg-[#111111] border border-[#222222] rounded-lg p-1 mb-6 w-fit">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key);
              clearResult();
            }}
            className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? "bg-[#222222] text-[#F8F8F7]"
                : "text-[#888888] hover:text-[#CCCCCC]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Panel */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          {activeTab === "create" && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-[#00B3E0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                <h2 className="text-lg font-semibold text-[#F8F8F7]">Create Invitation</h2>
              </div>
              <p className="text-xs text-[#666666] mb-5">POST /invitations</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#CCCCCC] mb-1.5">
                    Inviter UMA <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={inviterUma}
                    onChange={(e) => setInviterUma(e.target.value)}
                    placeholder="$alice@partner.grid.com"
                    className="w-full bg-black border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2.5 outline-none focus:border-[#00B3E0] placeholder-[#555555]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#CCCCCC] mb-1.5">
                    First Name <span className="text-[#666666]">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Alice"
                    className="w-full bg-black border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2.5 outline-none focus:border-[#00B3E0] placeholder-[#555555]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#CCCCCC] mb-1.5">
                    Amount to Send <span className="text-[#666666]">(optional, minor units)</span>
                  </label>
                  <input
                    type="text"
                    value={amountToSend}
                    onChange={(e) => setAmountToSend(e.target.value.replace(/[^0-9]/g, ""))}
                    placeholder="10000"
                    className="w-full bg-black border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2.5 outline-none focus:border-[#00B3E0] placeholder-[#555555]"
                  />
                </div>
                <button
                  onClick={handleCreate}
                  disabled={!inviterUma.trim() || loading}
                  className="w-full py-2.5 rounded-lg text-sm font-medium bg-[#00B3E0] text-black hover:bg-[#00C4F5] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Creating..." : "Create Invitation"}
                </button>
              </div>
            </>
          )}

          {activeTab === "lookup" && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-[#00B3E0]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
                <h2 className="text-lg font-semibold text-[#F8F8F7]">Look Up Invitation</h2>
              </div>
              <p className="text-xs text-[#666666] mb-5">GET /invitations/&#123;code&#125;</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#CCCCCC] mb-1.5">
                    Invitation Code <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={lookupCode}
                    onChange={(e) => setLookupCode(e.target.value)}
                    placeholder="Enter invitation code"
                    className="w-full bg-black border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2.5 outline-none focus:border-[#00B3E0] placeholder-[#555555]"
                  />
                </div>
                <button
                  onClick={handleLookup}
                  disabled={!lookupCode.trim() || loading}
                  className="w-full py-2.5 rounded-lg text-sm font-medium bg-[#00B3E0] text-black hover:bg-[#00C4F5] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Looking up..." : "Look Up"}
                </button>
              </div>
            </>
          )}

          {activeTab === "claim" && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <h2 className="text-lg font-semibold text-[#F8F8F7]">Claim Invitation</h2>
              </div>
              <p className="text-xs text-[#666666] mb-5">POST /invitations/&#123;code&#125;/claim</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#CCCCCC] mb-1.5">
                    Invitation Code <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={claimCode}
                    onChange={(e) => setClaimCode(e.target.value)}
                    placeholder="Enter invitation code"
                    className="w-full bg-black border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2.5 outline-none focus:border-[#00B3E0] placeholder-[#555555]"
                  />
                </div>
                <div>
                  <label className="block text-sm text-[#CCCCCC] mb-1.5">
                    Invitee UMA <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={inviteeUma}
                    onChange={(e) => setInviteeUma(e.target.value)}
                    placeholder="$newuser@uma.me"
                    className="w-full bg-black border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2.5 outline-none focus:border-[#00B3E0] placeholder-[#555555]"
                  />
                </div>
                <button
                  onClick={handleClaim}
                  disabled={!claimCode.trim() || !inviteeUma.trim() || loading}
                  className="w-full py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Claiming..." : "Claim Invitation"}
                </button>
              </div>
            </>
          )}

          {activeTab === "cancel" && (
            <>
              <div className="flex items-center gap-2 mb-1">
                <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
                </svg>
                <h2 className="text-lg font-semibold text-[#F8F8F7]">Cancel Invitation</h2>
              </div>
              <p className="text-xs text-[#666666] mb-5">POST /invitations/&#123;code&#125;/cancel</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-[#CCCCCC] mb-1.5">
                    Invitation Code <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={cancelCode}
                    onChange={(e) => setCancelCode(e.target.value)}
                    placeholder="Enter invitation code"
                    className="w-full bg-black border border-[#222222] rounded text-sm text-[#F8F8F7] px-3 py-2.5 outline-none focus:border-[#00B3E0] placeholder-[#555555]"
                  />
                </div>
                <button
                  onClick={handleCancel}
                  disabled={!cancelCode.trim() || loading}
                  className="w-full py-2.5 rounded-lg text-sm font-medium bg-red-500 text-white hover:bg-red-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? "Cancelling..." : "Cancel Invitation"}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Result Panel */}
        <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
          <h2 className="text-lg font-semibold text-[#F8F8F7] mb-4">Result</h2>

          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-[#222222] border-t-[#00B3E0] rounded-full animate-spin" />
            </div>
          )}

          {error && !loading && (
            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
              <div className="text-sm font-medium text-red-400 mb-1">Error</div>
              <pre className="text-xs font-mono text-red-300 whitespace-pre-wrap">{error}</pre>
            </div>
          )}

          {result && !loading && (
            <div className="space-y-4">
              {/* Status Badge */}
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded text-xs font-semibold ${
                    statusColors[result.status]?.bg ?? "bg-[#222222]"
                  } ${statusColors[result.status]?.text ?? "text-[#888888]"}`}
                >
                  {result.status}
                </span>
                <span className="text-xs text-[#666666]">
                  {formatDate(result.createdAt)}
                </span>
              </div>

              {/* Detail Rows */}
              <div className="space-y-3">
                <div className="flex justify-between items-start py-2 border-b border-[#222222]">
                  <span className="text-xs text-[#888888]">Code</span>
                  <code className="text-sm font-mono text-[#F8F8F7]">{result.code}</code>
                </div>

                <div className="flex justify-between items-start py-2 border-b border-[#222222]">
                  <span className="text-xs text-[#888888]">Inviter UMA</span>
                  <span className="text-sm font-mono text-[#00B3E0]">{result.inviterUma}</span>
                </div>

                {result.inviteeUma && (
                  <div className="flex justify-between items-start py-2 border-b border-[#222222]">
                    <span className="text-xs text-[#888888]">Invitee UMA</span>
                    <span className="text-sm font-mono text-[#CCCCCC]">{result.inviteeUma}</span>
                  </div>
                )}

                {result.firstName && (
                  <div className="flex justify-between items-start py-2 border-b border-[#222222]">
                    <span className="text-xs text-[#888888]">First Name</span>
                    <span className="text-sm text-[#CCCCCC]">{result.firstName}</span>
                  </div>
                )}

                {result.amountToSend != null && (
                  <div className="flex justify-between items-start py-2 border-b border-[#222222]">
                    <span className="text-xs text-[#888888]">Amount to Send</span>
                    <span className="text-sm font-mono text-[#CCCCCC]">{result.amountToSend}</span>
                  </div>
                )}

                {result.url && (
                  <div className="flex flex-col gap-1 py-2 border-b border-[#222222]">
                    <span className="text-xs text-[#888888]">Invitation URL</span>
                    <a
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-mono text-[#00B3E0] hover:underline break-all"
                    >
                      {result.url}
                    </a>
                  </div>
                )}
              </div>

              {/* Raw JSON */}
              <details className="mt-4">
                <summary className="text-xs text-[#666666] cursor-pointer hover:text-[#888888]">
                  Raw JSON Response
                </summary>
                <pre className="mt-2 p-3 rounded-lg bg-black border border-[#222222] text-xs font-mono text-[#CCCCCC] whitespace-pre-wrap overflow-auto max-h-64">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </details>
            </div>
          )}

          {!result && !error && !loading && (
            <div className="flex flex-col items-center justify-center py-12 text-[#555555]">
              <svg className="w-10 h-10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>
              <p className="text-sm">Submit a form to see results here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
